import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { dropdown, cn } from './uiTheme';
import { dropdownPortalPosition, isDropdownPortalTarget, stopModalScrollLockPropagation } from './dropdownPortal';
import DropdownSearchPortalLayer from './DropdownSearchPortalLayer';

/**
 * Reusable dropdown with search/filter. User types to filter options and selects from the list.
 */
function DropdownSearch({
  options = [],
  value,
  onChange,
  getOptionValue = (o) => o.value,
  getOptionLabel = (o) => o.label,
  getOptionSublabel,
  placeholder = 'Search…',
  emptyMessage = 'No matches',
  disabled = false,
  id,
  className = '',
  inputClassName = '',
  compact = false,
  usePortal = false,
  onOpen,
  onClose,
}: any) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [portalStyle, setPortalStyle] = useState<any>({});
  const wrapperRef = useRef<any>(null);
  const panelRef = useRef<any>(null);
  const inputRef = useRef<any>(null);
  const wasOpenRef = useRef(false);

  const valueKey =
    value == null
      ? null
      : typeof value === 'object' && value !== null
        ? getOptionValue(value)
        : value;
  const selectedOption =
    value == null
      ? null
      : (options.find((o) => getOptionValue(o) === valueKey) ??
        (typeof value === 'object' && value !== null ? value : null));
  const displayLabel = selectedOption ? getOptionLabel(selectedOption) : '';

  const queryLower = (query || '').trim().toLowerCase();
  const filtered = !queryLower.length
    ? options
    : options.filter((o) => {
        const label = getOptionLabel(o);
        const sub = getOptionSublabel ? getOptionSublabel(o) : '';
        return (
          (label && label.toLowerCase().includes(queryLower)) ||
          (sub && sub.toLowerCase().includes(queryLower))
        );
      });

  useEffect(() => {
    if (!open) {
      setQuery('');
      setHighlightIndex(0);
    } else {
      inputRef.current?.focus();
    }
  }, [open]);

  useEffect(() => {
    setHighlightIndex(0);
  }, [query]);

  useEffect(() => {
    if (open && !wasOpenRef.current) onOpen?.();
    if (!open && wasOpenRef.current) onClose?.();
    wasOpenRef.current = open;
  }, [open, onOpen, onClose]);

  const updatePortalPosition = useRef(() => {});
  updatePortalPosition.current = () => {
    if (!usePortal || !wrapperRef.current) return;
    setPortalStyle(dropdownPortalPosition(wrapperRef.current.getBoundingClientRect()));
  };

  useLayoutEffect(() => {
    if (!open || !usePortal) return;
    const run = () => updatePortalPosition.current();
    run();
    window.addEventListener('scroll', run, true);
    window.addEventListener('resize', run);
    return () => {
      window.removeEventListener('scroll', run, true);
      window.removeEventListener('resize', run);
    };
  }, [open, usePortal, query, options.length]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      const t = e.target;
      if (wrapperRef.current?.contains(t)) return;
      if (usePortal && panelRef.current?.contains(t)) return;
      if (usePortal && isDropdownPortalTarget(t)) return;
      setOpen(false);
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, usePortal]);

  useEffect(() => {
    if (!open || !panelRef.current) return;
    const el = panelRef.current.querySelector('[data-highlighted="true"]');
    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [highlightIndex, open]);

  const handleKeyNav = (e) => {
    if (!open) {
      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => (i < filtered.length - 1 ? i + 1 : 0));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => (i > 0 ? i - 1 : filtered.length - 1));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const option = filtered[highlightIndex];
      if (option) {
        onChange(option);
        setOpen(false);
      }
    }
  };

  const handleSelect = (option) => {
    onChange(option);
    setOpen(false);
  };

  const handleControlBarClick = (e) => {
    if (disabled) return;
    const t = e.target;
    if (inputRef.current && (t === inputRef.current || inputRef.current.contains(t))) return;
    setOpen((o) => !o);
  };

  const controlBarClass = cn(
    'flex w-full items-center border border-dark-border bg-dark-elevated transition-all duration-200 ease-out origin-center',
    open && 'ring-2 ring-brand-purple/50 border-brand-purple',
    compact ? 'min-h-[36px] rounded-lg' : 'min-h-[42px] rounded-xl',
    disabled
      ? 'opacity-60 cursor-not-allowed'
      : 'cursor-text motion-safe:hover:border-dark-border-glow'
  );

  const inputPadClass = compact ? 'py-2 px-2.5 text-sm' : 'py-2.5 px-3 text-sm';
  const iconClass = compact
    ? 'w-3.5 h-3.5 flex-shrink-0 ml-2 text-slate-400 pointer-events-none'
    : 'w-4 h-4 flex-shrink-0 ml-3 text-slate-400 pointer-events-none';
  const chevronMr = compact ? 'mr-2' : 'mr-3';
  const listItemPad = compact ? 'px-3 py-2 text-left text-sm' : 'px-4 py-2.5 text-left text-sm';
  const listEmptyPad = compact ? 'px-3 py-2 text-sm' : 'px-4 py-3 text-sm';

  const listboxClassName = cn(
    dropdown.container,
    'overflow-y-auto overscroll-contain py-1 shadow-xl shadow-black/50',
    compact ? 'rounded-lg' : 'rounded-xl',
    !usePortal && 'absolute z-20 mt-1 w-full max-h-60'
  );

  const listboxInner = (
    <>
      {filtered.length === 0 ? (
        <li className={cn(listEmptyPad, 'text-slate-400')}>{emptyMessage}</li>
      ) : (
        filtered.map((option, i) => {
          const optValue = getOptionValue(option);
          const isSelected = valueKey != null && optValue === valueKey;
          const isHighlighted = i === highlightIndex;
          return (
            <li
              key={String(optValue)}
              role="option"
              aria-selected={isSelected}
              data-highlighted={isHighlighted}
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSelect(option);
              }}
              onMouseEnter={() => setHighlightIndex(i)}
              className={cn(
                'cursor-pointer transition-colors',
                listItemPad,
                isHighlighted || isSelected ? dropdown.itemActive : dropdown.item
              )}
            >
              <span className="font-medium block truncate">{getOptionLabel(option)}</span>
              {getOptionSublabel && getOptionSublabel(option) && (
                <span className="text-xs text-slate-500 truncate block mt-0.5">
                  {getOptionSublabel(option)}
                </span>
              )}
            </li>
          );
        })
      )}
    </>
  );

  const listbox = (
    <AnimatePresence initial={false}>
      {open ? (
        <motion.ul
          id={id ? `${id}-listbox` : undefined}
          ref={panelRef}
          role="listbox"
          onWheel={stopModalScrollLockPropagation}
          onTouchMove={stopModalScrollLockPropagation}
          className={listboxClassName}
          style={
            usePortal && portalStyle?.maxHeight != null
              ? { maxHeight: portalStyle.maxHeight }
              : undefined
          }
          initial={{ opacity: 0, scale: 0.95, y: -6 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -6 }}
          transition={{ type: 'spring', stiffness: 400, damping: 30 }}
        >
          {listboxInner}
        </motion.ul>
      ) : null}
    </AnimatePresence>
  );

  const portaledListbox =
    usePortal && typeof document !== 'undefined'
      ? createPortal(
          <DropdownSearchPortalLayer style={portalStyle}>{listbox}</DropdownSearchPortalLayer>,
          document.body
        )
      : null;

  return (
    <div className={cn('relative', className)} ref={wrapperRef}>
      <div className={controlBarClass} onClick={handleControlBarClick}>
        <Search className={iconClass} />
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={open ? query : displayLabel}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => !disabled && setOpen(true)}
          onKeyDown={handleKeyNav}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={!open}
          className={cn(
            'flex-1 min-w-0 bg-transparent border-0 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-0',
            inputPadClass,
            inputClassName
          )}
          autoComplete="off"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={id ? `${id}-listbox` : undefined}
          role="combobox"
        />
        <ChevronDown
          className={cn(
            'flex-shrink-0 text-slate-400 transition-transform pointer-events-none',
            compact ? 'w-3.5 h-3.5' : 'w-4 h-4',
            chevronMr,
            open && 'rotate-180'
          )}
        />
      </div>
      {portaledListbox ?? (!usePortal ? listbox : null)}
    </div>
  );
}

export default DropdownSearch;
